import { escapeCsvValue } from "utils/utils";
import {
  normalizeMasterFormatCodeCell,
  normalizeUniFormatCodeCell
} from "pages/design-scope-page/shared/classification-code-spacing";

type CodeColumn = "uf" | "mf";

function codeColumn(header: string): CodeColumn | null {
  const key = header.trim().toLowerCase().replace(/\s+/g, " ");
  if (
    key === "uniformat code" ||
    key === "uniformat" ||
    key === "uni-format code" ||
    key === "uni-format"
  ) {
    return "uf";
  }
  if (
    key === "masterformat code" ||
    key === "masterformat" ||
    key === "master-format code" ||
    key === "master-format"
  ) {
    return "mf";
  }
  return null;
}

/** RFC-style CSV rows. Quoted commas and doubled quotes are preserved. */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ",") {
      row.push(cell);
      cell = "";
      continue;
    }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function serializeCsvRows(rows: string[][]): string {
  return rows
    .map((row) => row.map((cell) => escapeCsvValue(cell)).join(","))
    .join("\n");
}

/**
 * Rewrite UniFormat and MasterFormat code cells so spacing matches the stored
 * catalog form. Returns the original text when there is nothing to change.
 */
export function normalizeDesignScopeImportCsv(text: string): string {
  const rows = parseCsvRows(text);
  const headerIndex = rows.findIndex((row) =>
    row.some((cell) => codeColumn(cell))
  );
  if (headerIndex < 0) return text;

  const columns = rows[headerIndex].map((cell) => codeColumn(cell));
  let changed = false;
  const nextRows = rows.map((row, rowIndex) => {
    if (rowIndex <= headerIndex) return row;
    return row.map((cell, columnIndex) => {
      const kind = columns[columnIndex];
      if (!kind) return cell;
      const next =
        kind === "uf"
          ? normalizeUniFormatCodeCell(cell)
          : normalizeMasterFormatCodeCell(cell);
      if (next !== cell) changed = true;
      return next;
    });
  });

  if (!changed) return text;
  return serializeCsvRows(nextRows);
}

export async function fileWithNormalizedClassificationCodes(
  file: File
): Promise<File> {
  let text: string;
  try {
    text = await file.text();
  } catch {
    return file;
  }
  const normalized = normalizeDesignScopeImportCsv(text);
  if (normalized === text) return file;
  return new File([normalized], file.name, {
    type: file.type || "text/csv",
    lastModified: file.lastModified
  });
}
